import * as numbers from "common/numbers";
import { isCoverageSplit } from "domain/towerResolvers";

const ALL_KEY = "ALL";

const isSimple = (retention) => retention?.retentionType === "SIMPLE";

export const coverageKeyOf = (retention) =>
  isSimple(retention)
    ? retention?.lossType
    : retention?.retentionCoverageCategory;

const displayOf = (key, map) => (key === ALL_KEY ? "All" : map?.[key] || key);

export const retentionLabel = (
  retention,
  { lossTypeMap, coverageMap } = {}
) => {
  const key = coverageKeyOf(retention);
  if (!key) {
    return "SIR";
  }
  return displayOf(key, isSimple(retention) ? lossTypeMap : coverageMap);
};

const hasPositiveValue = (retention) =>
  Number.isFinite(retention?.value) && retention.value > 0;

const matchTermsOf = (retention, { lossTypeMap, coverageMap } = {}) => {
  const key = coverageKeyOf(retention);
  const display = key
    ? (isSimple(retention) ? lossTypeMap : coverageMap)?.[key]
    : null;
  return [display, key]
    .filter((term) => typeof term === "string" && term)
    .map((term) => term.toLowerCase());
};

export const buildRetentionEntries = (retentions, maps = {}) =>
  (retentions || []).filter(hasPositiveValue).map((retention) => ({
    id: retention.id,
    value: retention.value,
    label: retentionLabel(retention, maps),
    coverageKey: coverageKeyOf(retention),
    matchTerms: matchTermsOf(retention, maps),
  }));

export const shareCoverageTokens = (coverage) =>
  typeof coverage === "string"
    ? coverage
        .split(/[,/;&]/)
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token)
    : [];

export const matchShareIndex = (entry, shares) => {
  if (entry?.coverageKey === ALL_KEY) {
    return -1;
  }
  const terms = entry?.matchTerms || [];
  return (shares || []).findIndex((share) =>
    shareCoverageTokens(share?.coverage).some((token) => terms.includes(token))
  );
};

export const MIN_SIR_PX = 4;
export const SIR_BAND_MAX_PX = 120;

const isSynthesisedBase = (layer) =>
  layer?.id == null &&
  layer?.attachment === 0 &&
  layer?.shares?.length === 1 &&
  layer.shares[0]?.carrier == null;

export const findBaseLayer = (layers) =>
  (layers || [])
    .filter((layer) => layer && !isSynthesisedBase(layer))
    .reduce(
      (lowest, layer) =>
        lowest == null || (layer.attachment ?? 0) < (lowest.attachment ?? 0)
          ? layer
          : lowest,
      null
    );

export const buildBand = (layers, entries) => {
  const base = findBaseLayer(layers);
  const all = entries || [];
  if (!isCoverageSplit(base?.shares)) {
    return { columns: [], unmatched: [...all] };
  }
  const shares = base.shares;
  const columns = shares.map(() => ({
    widthPct: 100 / shares.length,
    entries: [],
  }));
  const unmatched = [];
  all.forEach((entry) => {
    const index = matchShareIndex(entry, shares);
    if (index >= 0) {
      columns[index].entries.push(entry);
    } else {
      unmatched.push(entry);
    }
  });
  return { columns, unmatched };
};

export const boxHeightPx = (value, largestValue) =>
  Math.max(MIN_SIR_PX, (SIR_BAND_MAX_PX * value) / largestValue);

export const formatAmount = (value) =>
  Number.isFinite(value) ? `$${numbers.abbreviated(value, 3)}` : "–";

export const buildTooltipLines = (layer, entries) => {
  const shares = layer?.shares;
  const split = isCoverageSplit(shares);
  return (entries || []).map((entry) => {
    const index = split ? matchShareIndex(entry, shares) : -1;
    const limit = index >= 0 ? shares[index].shareOfLimit : layer?.limit;
    const attachment = (layer?.attachment ?? 0) + entry.value;
    return `${entry.label}: ${formatAmount(limit)} xs ${formatAmount(
      attachment
    )}`;
  });
};
